// Purpose: Frontend utility for authentication operations
// Follows the same serverless API pattern as the rental agreement utility.

import { debugLog } from './debug';

/**
 * Login with username and password
 * @param {string} username - Admin username
 * @param {string} password - Admin password
 * @returns {Promise<{token: string, expiresIn: string}>} JWT token and expiration
 */
export const login = async (username, password) => {
  try {
    debugLog('Attempting login...');

    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Login error:', data.error);
      throw new Error(data.error || 'Login failed');
    }

    debugLog('Login successful');
    return {
      token: data.token,
      expiresIn: data.expiresIn
    };
  } catch (error) {
    console.error('Error during login:', error);
    throw error;
  }
};

/**
 * Verify a JWT token
 * @param {string} token - JWT token to verify
 * @returns {Promise<{valid: boolean, username: string, role: string}>} Token validation result
 */
export const verifyToken = async (token) => {
  try {
    const response = await fetch('/api/auth/verify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token })
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Token verification error:', data.error);
      return { valid: false };
    }

    return data;
  } catch (error) {
    console.error('Error during token verification:', error);
    return { valid: false };
  }
};

/**
 * Store token in localStorage
 * @param {string} token - JWT token to store
 */
export const storeToken = (token) => {
  localStorage.setItem('authToken', token);
};

/**
 * Get token from localStorage
 * @returns {string|null} JWT token or null if not found
 */
export const getToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Remove token from localStorage
 */
export const clearToken = () => {
  localStorage.removeItem('authToken');
};
